import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Link from '@material-ui/core/Link'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import clsx from 'clsx'
import React from 'react'
import { HeaderGroup } from 'react-table'
import { sharedTableStyles } from '.'
import CustomHoverTooltip from './CustomHoverTooltip'

const tableHeaderFilterRowStyles = makeStyles((theme: Theme) => ({
    tableResizer: {
        display: 'inline-block',
        right: 0,
        top: 0,
        position: 'absolute',
        cursor: 'col-resize',
        height: '100%',
        touchAction: 'none',
        width: 3,
        '&:hover': {
            backgroundColor: '#fff',
        },
    },
    tableSortContainer: {
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active.MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
            color: '#fff',
        },
    },
    headerTextFilterContainer: {
        height: '100%',
    },
    headerTextGridContainer: {
        height: 'inherit',
        backgroundColor: '#56ACF2',
        color: '#fff',
        '-moz-user-select': '-moz-none',
        '-khtml-user-select': 'none',
        '-webkit-user-select': 'none',
    },
    headerText: {
        textTransform: 'uppercase',
    },
    headerRow: {
        minHeight: 40,
        width: 'inherit',
    },
    headerFilterPaddingRight: {
        paddingRight: theme.spacing(2),
    },
    clearAllFilter: {
        fontSize: pxToRem(14),
    },
    clearFilterText: {
        cursor: 'pointer',
    },
    tableHeaderFilterRow: {
        '& .sticky-left-header': {
            boxShadow: '8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
        '& .sticky-right-header': {
            boxShadow: '-8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
        maxHeight: pxToRem(125),
    },
}))

export interface TableHeaderFilterRowProps<TData extends Object> {
    checkboxColumnWidth: number
    headerGroups: HeaderGroup<TData>[]
    showFilters: boolean
    clearAllFilters: (event: React.MouseEvent) => void
    isImportTable?: boolean
}

const TableHeaderFilterRow = <TData extends Object>({
    checkboxColumnWidth,
    headerGroups,
    showFilters,
    clearAllFilters,
    isImportTable,
}: TableHeaderFilterRowProps<TData>) => {
    const tableHeaderFilterRowClasses = tableHeaderFilterRowStyles()
    const sharedTableClasses = sharedTableStyles()

    return (
        <>
            {headerGroups.map((headerGroup) => {
                let leftStickyColumnIndex = 0,
                    rightStickyColumnIndex = 0
                return (
                    <div
                        {...headerGroup.getHeaderGroupProps()}
                        className={clsx(
                            'tr',
                            tableHeaderFilterRowClasses.tableHeaderFilterRow
                        )}
                    >
                        {headerGroup.headers.map((column, index) => {
                            if (column.sticky === 'left') {
                                leftStickyColumnIndex = index
                            } else if (
                                column.sticky === 'right' &&
                                !rightStickyColumnIndex
                            ) {
                                rightStickyColumnIndex = index
                            }

                            const header = column.render('Header')

                            return (
                                <div
                                    {...column.getHeaderProps({
                                        style: {
                                            padding: 'unset',
                                            height: 'inherit',
                                            position: !column.sticky
                                                ? 'relative'
                                                : 'sticky',
                                            minWidth:
                                                column.sticky === 'left'
                                                    ? column?.stickyLeftMargin &&
                                                      column?.stickyLeftMargin! >
                                                          0
                                                        ? column.stickyLeftMargin
                                                        : checkboxColumnWidth
                                                    : (column as any)?.style
                                                    ? (column as any)?.style
                                                          ?.minWidth
                                                    : 'unset',
                                            left:
                                                column.sticky === 'left'
                                                    ? column.stickyLeftMargin ??
                                                      checkboxColumnWidth
                                                    : 'unset',
                                            right:
                                                column.sticky === 'right'
                                                    ? column.stickyRightMargin ??
                                                      0
                                                    : 'unset',
                                            zIndex: !column.sticky ? 0 : 1,
                                        },
                                    })}
                                    className={clsx(
                                        'th',
                                        leftStickyColumnIndex === index &&
                                            column.sticky === 'left' &&
                                            'sticky-left-header',
                                        rightStickyColumnIndex === index &&
                                            column.sticky === 'right' &&
                                            'sticky-right-header'
                                    )}
                                >
                                    <GridContainer
                                        direction="column"
                                        className={
                                            tableHeaderFilterRowClasses.headerTextFilterContainer
                                        }
                                    >
                                        <GridItem
                                            className={
                                                tableHeaderFilterRowClasses.headerRow
                                            }
                                            style={{ flex: 1 }}
                                        >
                                            <GridContainer
                                                {...column.getSortByToggleProps(
                                                    {
                                                        title: undefined,
                                                    }
                                                )}
                                                justify="space-between"
                                                alignItems="center"
                                                className={clsx(
                                                    tableHeaderFilterRowClasses.headerTextGridContainer,
                                                    sharedTableClasses.cellPadding
                                                )}
                                                style={{
                                                    minHeight: 40,
                                                    height: '100%',
                                                    cursor: isImportTable
                                                        ? 'default'
                                                        : column.canSort
                                                        ? 'pointer'
                                                        : 'default',
                                                }}
                                            >
                                                <GridItem
                                                    xs={
                                                        column.canSort ? 11 : 12
                                                    }
                                                    className={clsx(
                                                        sharedTableClasses.textOverflowEllipsis,
                                                        tableHeaderFilterRowClasses.headerText,
                                                        'header-text-container'
                                                    )}
                                                >
                                                    {typeof header ===
                                                    'string' ? (
                                                        <CustomHoverTooltip
                                                            text={header}
                                                        />
                                                    ) : (
                                                        header
                                                    )}
                                                </GridItem>
                                                {column.canSort && (
                                                    <GridItem
                                                        xs={1}
                                                        className={clsx(
                                                            tableHeaderFilterRowClasses.tableSortContainer,
                                                            'sort-label-container'
                                                        )}
                                                    >
                                                        {column.id !==
                                                            'selection' && (
                                                            <TableSortLabel
                                                                active={
                                                                    column.isSorted
                                                                }
                                                                direction={
                                                                    column.isSortedDesc
                                                                        ? 'desc'
                                                                        : 'asc'
                                                                }
                                                            />
                                                        )}
                                                    </GridItem>
                                                )}
                                            </GridContainer>
                                        </GridItem>
                                        {showFilters && (
                                            <GridItem
                                                className={clsx(
                                                    tableHeaderFilterRowClasses.headerRow,
                                                    sharedTableClasses.cellPadding,
                                                    tableHeaderFilterRowClasses.headerFilterPaddingRight,
                                                    'header-filter-container',
                                                    column.id === 'actionCol' &&
                                                        tableHeaderFilterRowClasses.clearAllFilter
                                                )}
                                            >
                                                {column.canFilter &&
                                                    column.render('Filter')}
                                                {column.id === 'actionCol' &&
                                                    !!clearAllFilters && (
                                                        <Link
                                                            underline="none"
                                                            onClick={
                                                                clearAllFilters
                                                            }
                                                            className={clsx(
                                                                tableHeaderFilterRowClasses.clearFilterText,
                                                                'clear-all-filters'
                                                            )}
                                                        >
                                                            Clear Filters
                                                        </Link>
                                                    )}
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                    {column.canResize && (
                                        <div
                                            {...column.getResizerProps()}
                                            className={clsx(
                                                tableHeaderFilterRowClasses.tableResizer,
                                                column.isResizing && 'resizing',
                                                'header-resize-container'
                                            )}
                                        />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )
            })}
        </>
    )
}

export default TableHeaderFilterRow
